import { COMPLETED } from 'customer-data-objects/engagement/EngagementStatusTypes';
import { getProperty } from 'customer-data-objects/record/ObjectRecordAccessors';
import { STATUS } from 'customer-data-objects/task/TaskPropertyNames';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback } from 'react';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import UILink from 'UIComponents/link/UILink';
import { UNASSOCIATED_TASK_MODAL } from '../constants/modalTypes';
import { useDispatch } from '../context/Redux';
import { TaskQueueManager } from '../lib/queues/TaskQueueManager';
import { closeModalAction, openModalAction } from '../redux/actions/overlay';
import { updateTempViewCrmSearchQuery } from '../redux/actions/tempViewState';
import { GROUP_NAMES, reportNewTasksError } from '../tracking/errors';
import { START_QUEUE_SOURCES, trackStartQueue } from '../tracking/tracker';
import { getObjectIdInListFilter, INCOMPLETE_TASK, isIncompleteTasksFilterApplied } from '../utils/crmSearchFilters';
import { addCreatedBeforeNowFilterToCRMSearchRequest, sortStateToCRMSearchSorts } from '../utils/crmSearchRequestBuilder';
import { fullHubSpotUrl, getIsHoldingMetaKey, navigateToURL, navigateToURLInNewTab } from '../utils/links';
import { useSortTable } from './table';
import { useSearchRequestOptionsForView } from './tasksSearchResults';
import { jsx as _jsx } from "react/jsx-runtime";
function showStartTaskQueueError({
  type = 'danger'
} = {}) {
  FloatingAlertStore.addAlert({
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "tasksAlerts.titles.genericError"
    }),
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "tasksAlerts.startTasksFailed"
    }),
    type
  });
}
function showStartTaskQueueOnCompletedTasksWarning(addIncompleteTasksFilter) {
  FloatingAlertStore.addAlert({
    message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      elements: {
        Link: UILink
      },
      message: "tasksAlerts.taskStatusFilterRequired.message_jsx",
      options: {
        onClick: addIncompleteTasksFilter
      }
    }),
    sticky: true,
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "tasksAlerts.taskStatusFilterRequired.title"
    }),
    id: 'task-status-filter-required',
    type: 'warning'
  });
}
const getIncompleteTasksOnlyFilters = ({
  crmSearchFilters
}) => {
  var _crmSearchFilters$;
  const filters = (crmSearchFilters === null || crmSearchFilters === void 0 || (_crmSearchFilters$ = crmSearchFilters[0]) === null || _crmSearchFilters$ === void 0 ? void 0 : _crmSearchFilters$.filters) || [];
  const filtersWithoutStatus = filters.filter(filter => filter.property !== STATUS);
  return [...filtersWithoutStatus, INCOMPLETE_TASK];
};
function startQueueAndFetchPageState({
  crmSearchQuery,
  firstTaskId
}) {
  const startTaskQueuePromise = TaskQueueManager.startTaskQueue({
    crmSearchQuery,
    redirectUrlForExitQueue: window.location.href,
    firstTaskId
  });
  if (firstTaskId) {
    return Promise.all([startTaskQueuePromise, TaskQueueManager.fetchPageStateForTask(firstTaskId)]);
  } else {
    return startTaskQueuePromise.then(queueState => {
      const {
        currentTaskId,
        isActive
      } = queueState;
      const fetchPageStatePromise = currentTaskId && isActive ? TaskQueueManager.fetchPageStateForTask(currentTaskId) : Promise.resolve({});
      return fetchPageStatePromise.then(pageStateForCurTask => [queueState, pageStateForCurTask]);
    });
  }
}
const handleStartQueueResponse = ({
  dispatch,
  shouldOpenInNewTab,
  source,
  viewId,
  taskId
}) => ([queueState, pageStateForCurTask]) => {
  const {
    currentTaskId,
    isActive
  } = queueState;
  const {
    shouldNavigateToUrl,
    shouldShowUnassociatedTaskModal,
    url,
    task
  } = pageStateForCurTask;

  // starting a queue can fail without error if the search just didn't return any results
  if (!currentTaskId || !isActive) {
    showStartTaskQueueError({
      type: 'warning'
    });
    return;
  }
  const completed = task ? getProperty(task, STATUS) === COMPLETED : undefined;

  // Make sure we track the queue before redirecting anywhere
  trackStartQueue({
    completed,
    source,
    viewId,
    taskId
  });

  // Tasks with associations will be routed to a record page
  if (shouldNavigateToUrl) {
    if (shouldOpenInNewTab) {
      navigateToURLInNewTab(fullHubSpotUrl(url));
    } else {
      navigateToURL(fullHubSpotUrl(url));
    }
    return;
  }

  // Unassociated tasks need to be handled through the unassociated task modal
  if (shouldShowUnassociatedTaskModal) {
    dispatch(openModalAction({
      modalType: UNASSOCIATED_TASK_MODAL,
      modalData: {
        shouldOpenInNewTab
      }
    }));
  } else {
    dispatch(closeModalAction());
  }
};
export function useStartQueueFromSelected({
  taskIds,
  viewId
}) {
  const dispatch = useDispatch();
  const {
    sortState
  } = useSortTable(viewId);
  const [firstTaskId] = taskIds;
  const startSelectedTasks = useCallback(() => {
    const shouldOpenInNewTab = getIsHoldingMetaKey();
    const crmSearchQuery = {
      // Hitting CRMSearch  isn't strictly necessary (we already know the list of tasks)
      // but the library makes a lot of assumptions that the queue is backed by CRMSearch.
      filterGroups: [{
        filters: [getObjectIdInListFilter(taskIds), INCOMPLETE_TASK]
      }],
      sorts: sortStateToCRMSearchSorts(sortState),
      count: taskIds.length,
      query: ''
    };
    return startQueueAndFetchPageState({
      crmSearchQuery,
      firstTaskId
    }).then(handleStartQueueResponse({
      dispatch,
      shouldOpenInNewTab,
      source: START_QUEUE_SOURCES.BULK_ACTION,
      taskId: firstTaskId,
      viewId
    })).catch(error => {
      console.error(error);
      showStartTaskQueueError();
      reportNewTasksError(error, {
        groupName: GROUP_NAMES.QUEUES,
        functionName: 'startTaskQueue',
        extra: {
          crmSearchQuery,
          redirectUrlForExitQueue: window.location.href,
          firstTaskId,
          source: START_QUEUE_SOURCES.BULK_ACTION,
          viewId
        }
      });
    });
  }, [dispatch, firstTaskId, sortState, taskIds, viewId]);
  return startSelectedTasks;
}
export function useStartTaskQueue({
  viewId
}) {
  const dispatch = useDispatch();
  const {
    crmSearchQuery: crmSearchQueryWithoutCreatedBeforeFilter
  } = useSearchRequestOptionsForView(viewId);
  const addIncompleteTasksFilter = useCallback(() => {
    const newFilters = getIncompleteTasksOnlyFilters({
      crmSearchFilters: crmSearchQueryWithoutCreatedBeforeFilter === null || crmSearchQueryWithoutCreatedBeforeFilter === void 0 ? void 0 : crmSearchQueryWithoutCreatedBeforeFilter.filterGroups
    });
    dispatch(updateTempViewCrmSearchQuery({
      viewId,
      crmSearchFilters: newFilters
    }));
  }, [crmSearchQueryWithoutCreatedBeforeFilter, dispatch, viewId]);
  const onStartTaskQueue = useCallback(({
    task = undefined,
    source
  }) => {
    var _crmSearchQueryWithou;
    const shouldOpenInNewTab = getIsHoldingMetaKey();
    const filters = (crmSearchQueryWithoutCreatedBeforeFilter === null || crmSearchQueryWithoutCreatedBeforeFilter === void 0 || (_crmSearchQueryWithou = crmSearchQueryWithoutCreatedBeforeFilter.filterGroups) === null || _crmSearchQueryWithou === void 0 ? void 0 : _crmSearchQueryWithou[0].filters) || [];
    const isOnlyIncompleteTasksShown = isIncompleteTasksFilterApplied(filters);
    if (!isOnlyIncompleteTasksShown) {
      showStartTaskQueueOnCompletedTasksWarning(addIncompleteTasksFilter);
      return Promise.resolve();
    }
    const firstTaskId = task ? task.objectId : undefined;
    const crmSearchQuery = addCreatedBeforeNowFilterToCRMSearchRequest(crmSearchQueryWithoutCreatedBeforeFilter);
    return startQueueAndFetchPageState({
      crmSearchQuery,
      firstTaskId
    }).then(handleStartQueueResponse({
      dispatch,
      shouldOpenInNewTab,
      source,
      viewId,
      taskId: firstTaskId
    })).catch(error => {
      console.error(error);
      showStartTaskQueueError();
      reportNewTasksError(error, {
        groupName: GROUP_NAMES.QUEUES,
        functionName: 'startTaskQueue',
        extra: {
          crmSearchQuery,
          redirectUrlForExitQueue: window.location.href,
          firstTaskId,
          source,
          viewId
        }
      });
    });
  }, [addIncompleteTasksFilter, dispatch, viewId, crmSearchQueryWithoutCreatedBeforeFilter]);
  return onStartTaskQueue;
}